import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Note, Project, Segment, Space } from "@scenix/core";
import { api } from "../lib/api";
import {
  isSelection,
  secondsToX,
  spanFromDrag,
  timecode,
  xToSeconds,
  type Span,
} from "../lib/scrub";

interface ScrubBarProps {
  /** Named because a segment's stills are served from the project (ADR 000070). */
  spaceId: Space["id"];
  projectId: Project["id"];
  segments: readonly Segment[];
  notes: readonly Note[];
  durationSec: number;
  /** The selection, owned by the parent so the menu and the bar agree on it. */
  selection: Span | null;
  onSelect: (span: Span | null) => void;
  /** Where playback is, in seconds. Null before anything has been rendered. */
  playheadSec: number | null;
  onScrub?: (seconds: number) => void;
  /**
   * Which segment the pointer is over, or null when it leaves the lane. The
   * parent decides what to show for it: a chain is routinely part storyboard
   * and part picture, and what is worth showing differs per segment
   * (ADR 000064).
   */
  onHover?: (segment: Segment | null) => void;
}

/**
 * The scrub bar: segments drawn in time, with a draggable range selection.
 *
 * One lane today. Track zero is picture and picture is the chain; audio and text
 * ride on tracks this does not draw yet (ADR 000025).
 *
 * It draws segments, never a rendered file. A chain is routinely part made, so
 * each segment is drawn according to its own state: the ready ones play, the
 * planned ones show their brief, and both are things a note can point at.
 */
export function ScrubBar({
  spaceId,
  projectId,
  segments,
  notes,
  durationSec,
  selection,
  onSelect,
  playheadSec,
  onScrub,
  onHover,
}: ScrubBarProps) {
  const lane = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [anchorSec, setAnchorSec] = useState<number | null>(null);

  // Measured rather than assumed, so the same selection survives a resize and
  // a sidebar opening beside it.
  useLayoutEffect(() => {
    const element = lane.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    setWidth(element.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  const secondsAt = useCallback(
    (clientX: number) => {
      const box = lane.current?.getBoundingClientRect();
      if (!box) return 0;
      return xToSeconds(clientX - box.left, durationSec, box.width);
    },
    [durationSec],
  );

  const toX = useCallback(
    (seconds: number) => secondsToX(seconds, durationSec, width),
    [durationSec, width],
  );

  // The drag continues on window, not on the lane. Releasing the mouse outside
  // the bar is normal when selecting right up to the end, and a listener on the
  // element alone would leave the drag stuck on.
  useEffect(() => {
    if (anchorSec === null) return;

    const move = (event: PointerEvent) =>
      onSelect(spanFromDrag(anchorSec, secondsAt(event.clientX)));
    const up = (event: PointerEvent) => {
      const span = spanFromDrag(anchorSec, secondsAt(event.clientX));
      setAnchorSec(null);
      // A click is a scrub, not an empty selection.
      if (isSelection(span)) onSelect(span);
      else {
        onSelect(null);
        onScrub?.(secondsAt(event.clientX));
      }
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [anchorSec, secondsAt, onSelect, onScrub]);

  if (durationSec <= 0) {
    return (
      <div className="scrub scrub-empty">
        <p className="muted">Nothing on the timeline yet.</p>
      </div>
    );
  }

  return (
    <div className="scrub">
      <div
        ref={lane}
        className="scrub-lane"
        role="slider"
        aria-label="Timeline"
        aria-valuemin={0}
        aria-valuemax={durationSec}
        aria-valuenow={playheadSec ?? 0}
        aria-valuetext={timecode(playheadSec ?? 0)}
        tabIndex={0}
        onPointerDown={(event) => {
          // Left button only: a right-click is for the menu on an existing
          // selection, and starting a drag would clear it first.
          if (event.button !== 0) return;
          event.preventDefault();
          const at = secondsAt(event.clientX);
          setAnchorSec(at);
          onSelect(null);
        }}
        onPointerLeave={() => onHover?.(null)}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 1 : 0.1;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            onScrub?.(Math.max(0, (playheadSec ?? 0) - step));
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            onScrub?.(Math.min(durationSec, (playheadSec ?? 0) + step));
          } else if (event.key === "Escape") {
            onSelect(null);
          }
        }}
      >
        {segments.map((segment) => (
          <div
            key={segment.id}
            className={`scrub-segment is-${segment.state}`}
            style={{
              left: `${toX(segment.startSec)}px`,
              width: `${Math.max(1, toX(segment.endSec) - toX(segment.startSec))}px`,
            }}
            title={describe(segment)}
            onPointerEnter={() => onHover?.(segment)}
          >
            {/*
             * A planned segment shows its brief: a still pulled from the
             * footage, or failing that the direction. That is what makes the
             * unmade part of a long video something you can watch past and
             * comment on rather than a hole (ADR 000025).
             */}
            {segment.brief.stills.length > 0 ? (
              <img
                src={api.segmentStillUrl(spaceId, projectId, segment.id, 0)}
                alt=""
                loading="lazy"
              />
            ) : (
              <span className="scrub-segment-label">
                {segment.label ||
                  segment.brief.direction ||
                  segment.brief.narration ||
                  sourceSummary(segment.brief)}
              </span>
            )}

            {/*
             * Marked, not hidden. A stale picture keeps playing because it is
             * out of date rather than absent, and saying so is the difference
             * between a guess and a claim (ADR 000026).
             */}
            {segment.state === "stale" && <span className="scrub-badge">brief changed</span>}
            {segment.state === "failed" && <span className="scrub-badge is-failed">failed</span>}
          </div>
        ))}

        {/* Notes sit under the segments as marks, so open work is visible
            without opening anything. */}
        <div className="scrub-notes">
          {notes
            .filter((note) => note.status === "open")
            .map((note) => (
              <span
                key={note.id}
                className={`scrub-note is-${note.kind}`}
                style={{
                  left: `${toX(note.range.startSec)}px`,
                  width: `${Math.max(2, toX(note.range.endSec) - toX(note.range.startSec))}px`,
                }}
                title={note.body || note.kind}
              />
            ))}
        </div>

        {selection && (
          <div
            className="scrub-selection"
            style={{
              left: `${toX(selection.startSec)}px`,
              width: `${Math.max(1, toX(selection.endSec) - toX(selection.startSec))}px`,
            }}
          />
        )}

        {playheadSec !== null && (
          <div className="scrub-playhead" style={{ left: `${toX(playheadSec)}px` }} />
        )}
      </div>

      <div className="scrub-ruler">
        <span>{timecode(0)}</span>
        <span>
          {selection ? `${timecode(selection.startSec)} to ${timecode(selection.endSec)}` : ""}
        </span>
        <span>{timecode(durationSec)}</span>
      </div>
    </div>
  );
}

/** What the segment is, for the hover title. */
function describe(segment: Segment): string {
  const what =
    segment.label ||
    segment.brief.direction ||
    segment.brief.narration ||
    sourceSummary(segment.brief);
  return segment.state === "planned" ? `${what} (not made yet)` : what;
}

/**
 * A fallback for a segment that has footage attached but no direction or label
 * yet, so it reads as "something is here" rather than a blank shot.
 */
function sourceSummary(brief: Segment["brief"]): string {
  if (brief.sources.length === 0) return "Empty segment";
  return brief.sources.length === 1 ? "1 source clip" : `${brief.sources.length} source clips`;
}
